<template>
  <div class="home-page">
    <b-overlay :show="isLoading || showLoading" variant="light" blur="2px" spinner-variant="primary">

      <section class="hero-section">
        <div class="hero-content">
          <p v-if="nomeUsuarioLogado" class="hero-greeting">Olá, {{ nomeUsuarioLogado }}!</p>
          <h1 class="hero-title">Portal B2B</h1>
          <p class="hero-subtitle">
            <template v-if="brandName && brandName !== 'Catálogo'">{{ brandName }} &mdash; </template>Catálogo de Produtos
          </p>
        </div>
      </section>

      <section class="catalog-section">

        <div class="catalog-action-bar">
          <div class="action-bar-filters">
            <div class="filter-field">
              <label class="filter-label">Nome do produto</label>
              <b-form-input
                v-model="currentItemNome"
                class="filter-input"
                placeholder="Digite o nome do produto"
                @keyup.enter="onPesquisar"
              />
            </div>

            <div class="filter-field">
              <label class="filter-label">Código do produto</label>
              <b-form-input
                v-model="currentItemCodigo"
                class="filter-input"
                placeholder="Digite o código (ISBN/SKU)"
                @keyup.enter="onPesquisar"
              />
            </div>

            <div class="filter-field" v-if="filtrosOptions.lingua.length">
              <label class="filter-label">Língua</label>
              <b-form-select v-model="filtroLingua" class="filter-input" :options="opcoesFiltro('lingua', 'Todas')" />
            </div>
            <div class="filter-field" v-if="filtrosOptions.segmento.length">
              <label class="filter-label">Segmento</label>
              <b-form-select v-model="filtroSegmento" class="filter-input" :options="opcoesFiltro('segmento', 'Todos')" />
            </div>
            <div class="filter-field" v-if="filtrosOptions.niveleducacional.length">
              <label class="filter-label">Nível educacional</label>
              <b-form-select v-model="filtroNivel" class="filter-input" :options="opcoesFiltro('niveleducacional', 'Todos')" />
            </div>
            <div class="filter-field" v-if="filtrosOptions.colecao.length">
              <label class="filter-label">Coleção</label>
              <b-form-select v-model="filtroColecao" class="filter-input" :options="opcoesFiltro('colecao', 'Todas')" />
            </div>
            <div class="filter-field" v-if="filtrosOptions.marcas.length > 1">
              <label class="filter-label">Marca</label>
              <b-form-select v-model="filtroMarca" class="filter-input" :options="opcoesMarcas" />
            </div>

            <div class="filter-buttons">
              <b-button
                class="btn-filter-action btn-primary-action"
                @click="onPesquisar"
                :disabled="!hasActiveFilters"
              >
                <feather-icon icon="SearchIcon" size="16" />
                <span>Buscar</span>
              </b-button>
              <b-button
                class="btn-filter-action btn-secondary-action"
                @click="onLimparFiltros"
                :disabled="!hasActiveFilters"
              >
                <feather-icon icon="XIcon" size="16" />
                <span>Limpar</span>
              </b-button>
            </div>
          </div>

          <div class="action-bar-right">
            <!-- Filtro por tipo de pedido (checkbox exclusivo). Só aparece quando o
                 perfil tem 2+ tipos e NÃO é perfil Cliente (cliente vê tudo junto). -->
            <div class="tipo-filter" v-if="showTipoFilter">
              <label
                v-for="t in tiposDisponiveis"
                :key="t.value"
                class="tipo-filter-item"
              >
                <input
                  type="checkbox"
                  :checked="tipoSelecionado === t.value"
                  @change="selecionarTipo(t)"
                >
                <span>{{ nomeTipo(t) }}</span>
              </label>
            </div>
            <span class="products-count" v-if="productsCount">
              <strong>{{ productsCount }}</strong>
              produto{{ productsCount !== 1 ? 's' : '' }}
              encontrado{{ productsCount !== 1 ? 's' : '' }}
            </span>
            <b-button
              class="btn-download"
              @click="abrirImportarCsv"
            >
              <feather-icon icon="UploadIcon" size="16" />
              <span>Importar CSV</span>
            </b-button>
            <!-- Exportar Catálogo: restrito a perfis administradores -->
            <b-button
              v-if="isPerfilAdmin"
              class="btn-download"
              @click="abrirExportarCatalogo"
            >
              <feather-icon icon="FileTextIcon" size="16" />
              <span>Exportar Catálogo</span>
            </b-button>
            <b-button
              v-if="hasCatalogPDF"
              class="btn-download"
              @click="downloadCatalogoPDF"
            >
              <feather-icon icon="DownloadIcon" size="16" />
              <span>Catálogo PDF</span>
            </b-button>
          </div>
        </div>

        <modal-importar-csv v-model="showImportarCsv" />
        <modal-exportar-catalogo v-model="showExportarCatalogo" />

        <div class="products-section" v-if="!showLoading">
          <p v-if="!contextoPrecoPronto" role="status">Selecione o cliente e os dados do pedido para consultar os valores.</p>
          <p v-else-if="precosCarregando" role="status">Calculando valores dos produtos desta página…</p>
          <div v-if="precosOcultos" class="d-flex justify-content-end mb-1">
            <button
              type="button"
              class="btn btn-link btn-sm text-muted p-50"
              title="Recarregar"
              aria-label="Recarregar preços dos produtos"
              :disabled="precosCarregando"
              @click="carregarPrecosCatalogo"
            >
              <feather-icon icon="RefreshCwIcon" size="16" aria-hidden="true" />
            </button>
          </div>
          <div class="products-grid" v-if="productsCount">
            <div
              v-for="item in produtosComPreco"
              :key="item.item_id || item.itemId || item.CodItem"
              class="product-grid-item"
            >
              <production-item
                :produto="item"
                :preco="precosCatalogo[item.item_id || item.itemId || item.CodItem]"
                :estoque-info="estoqueMap[item.item_id || item.itemId || item.CodItem]"
              />
            </div>
          </div>

          <div class="empty-state" v-else>
            <div class="empty-icon">
              <feather-icon icon="PackageIcon" size="56" />
            </div>
            <h3 class="empty-title">Nenhum produto encontrado</h3>
            <p class="empty-text">Nenhum produto disponível para o tipo de pedido selecionado. Tente ajustar os filtros.</p>
            <b-button class="btn-reset" @click="onLimparFiltros" v-if="hasActiveFilters">
              <feather-icon icon="RefreshCwIcon" size="16" />
              <span>Limpar Filtros</span>
            </b-button>
          </div>
        </div>

        <div class="catalog-pagination" v-if="productsCount && totalPages > 1 && !showLoading">
          <span class="pagination-info">
            Página <strong>{{ currentPage }}</strong> de <strong>{{ totalPages }}</strong>
            · {{ totalProducts || productsCount }} produto{{ (totalProducts || productsCount) !== 1 ? 's' : '' }}
          </span>
          <b-pagination
            v-model="currentPage"
            :total-rows="totalProducts || productsCount"
            :per-page="pageSize"
            first-number
            last-number
            align="center"
            class="mb-0"
            @change="onChangePage"
          />
        </div>

      </section>

      <view-pic-generica v-if="picAtivo && picGenereca.length > 0" />

    </b-overlay>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import SLSMarcaService from '@/services/SistemaDKP/sls-marca.service';
import SLSEsPrioriService from '@/services/SistemaDKP/SLSEsPriori.service';
import SLSParametrosService from '@/services/SistemaDKP/SLSParametros.service';
import SLSEstoqueService from '@/services/SistemaDKP/SLSEstoque.service';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import ViewPicGenerica from '@/views/home/components/viewPicGenerica.vue';
import ModalImportarCsv from '@/views/home/components/ModalImportarCsv.vue';
import ModalExportarCatalogo from '@/views/home/components/ModalExportarCatalogo.vue';
import HomeService from '@/services/home.service';
import InitialDataService from '@/services/InitialData.service';
import SLSPedidosVendasService from '@/services/Operacoes/sls_pedidoVendas.service';
import Swal from 'sweetalert2';
import axios from '@/libs/axios';
import { cotarProdutoCatalogo } from '@/utils/catalogoPrecos';
import { isPerfilAdminAtivo, isPerfilClienteAtivo, nomeCategoria } from '@/libs/perfilCliente';

const slsEsPrioriService = new SLSEsPrioriService();
const slsParametrosService = new SLSParametrosService();
const estoqueService = new SLSEstoqueService();
const homeService = new HomeService();

export default {
  name: 'CatalogoProdutos',
  components: {
    ViewPicGenerica,
    ModalImportarCsv,
    ModalExportarCatalogo,
    ProductionItem: () => import('@/views/app/CartECommerce/catalogo-produtos/ProductionItem.vue'),
  },
  data() {
    return {
      showLoading: false,
      codMarca: null,
      currentItemNome: '',
      currentItemCodigo: '',
      filtroLingua: null,
      filtroSegmento: null,
      filtroNivel: null,
      filtroColecao: null,
      filtroMarca: null,
      filtrosOptions: {
        lingua: [], segmento: [], niveleducacional: [], colecao: [], marcas: [],
      },
      slsMarcaService: new SLSMarcaService(),
      currentPage: 1,
      pageSize: 12,
      lastFetchCount: 0,
      totalProducts: 0,
      estoqueMap: {},
      precosCatalogo: {},
      precosCarregando: false,
      precoEpoch: 0,
      showImportarCsv: false,
      showExportarCatalogo: false,
      catalogoPdfAtivo: false,
      catalogoPdfCaminho: '',
    };
  },
  computed: {
    itensPaginaPreco() {
      return this.$store.getters['slsMarca/getImagensPorMarca'] || [];
    },
    contextoPreco() {
      const form = this.$store.getters['InfoPedido/GET_FORM_INFO_PEDIDO'] || {};
      return {
        ...form,
        enderecoAlternativo: this.$store.getters['InfoPedido/GET_ENDERECO_ALTERNATIVO'],
      };
    },
    contextoPrecoPronto() {
      const f = this.contextoPreco;
      return !!(f.cliente?.clienteid && f.tabelaPreco?.tabelapreco_id
        && f.cliente?.clientenomeabreviado && f.estabelecimento?.estabelecimentoid
        && (!this.$store.getters['preferencias/condicaoPagamentoAtiva']
          || f.condicaoPagamento?.condicaopagamentoid));
    },
    chavePrecosCatalogo() {
      return JSON.stringify([this.$route.params.tenantSlug, this.contextoPreco,
        this.itensPaginaPreco.map(i => i.item_id || i.itemId || i.CodItem)]);
    },
    produtosComPreco() {
      if (!this.contextoPrecoPronto) return this.itensPaginaPreco;
      return this.itensPaginaPreco.filter(i => this.precosCatalogo[i.item_id || i.itemId || i.CodItem] > 0);
    },
    precosOcultos() {
      return Object.values(this.precosCatalogo).filter(v => v === null).length;
    },
    // Exportar Catálogo: restrito a perfis administradores
    isPerfilAdmin() {
      return isPerfilAdminAtivo();
    },
    // Perfil Cliente: catálogo exibe TODAS as categorias juntas (padrão +
    // certificação + doação, conforme os tipos permitidos) — a distinção só é
    // feita na hora de adicionar ao carrinho/operação.
    isPerfilCliente() {
      return isPerfilClienteAtivo();
    },
    // Tipos de pedido REAIS permitidos ao perfil (sem o placeholder "Selecione").
    tiposDisponiveis() {
      const tipos = this.$store.getters['InfoPedido/GET_TIPO_PEDIDOS_CLIENTE'] || [];
      return tipos.filter((t) => t && t.value !== null && t.value !== undefined);
    },
    tipoSelecionado() {
      return this.$store.getters['InfoPedido/GET_FORM_INFO_PEDIDO']
        ?.tipoPedidoCliente?.tipopedidoid || '';
    },
    // Filtro de tipo na home: só p/ perfis NÃO-cliente com 2+ tipos permitidos.
    showTipoFilter() {
      return !this.isPerfilCliente && this.tiposDisponiveis.length > 1;
    },
    ...mapGetters('PicStore', {
      picGenereca: 'GET_PIC_GENERICAS',
    }),
    // Funcionalidade PIC parametrizada por tenant (PIC_ATIVO, padrão desligado).
    picAtivo() {
      return this.$store.getters['preferencias/picAtivo'] === true;
    },
    ...mapGetters('InfoPedido', {
      isLoading: 'GET_IS_LOADING',
    }),
    ...mapGetters('slsMarca', {
      getCatalogMarca: 'getCurrentCatalogoMarca',
      getEsMarcas: 'getEsMarcas',
    }),
    brandName() {
      return this.getCatalogMarca?.des_marca || '';
    },
    // Saudação: nome do usuário logado (userData pode vir achatado ou aninhado)
    nomeUsuarioLogado() {
      try {
        const ud = JSON.parse(localStorage.getItem('userData') || 'null');
        const nome = ud?.nome || ud?.userData?.nome || '';
        return String(nome).trim().split(/\s+/)[0] || '';
      } catch (e) {
        return '';
      }
    },
    hasCatalogPDF() {
      return this.catalogoPdfAtivo && !!this.catalogoPdfCaminho;
    },
    productsCount() {
      return this.totalProducts || this.$store.getters['slsMarca/getImagensPorMarca']?.length || 0;
    },
    hasActiveFilters() {
      return !!(this.currentItemNome.trim() || this.currentItemCodigo.trim()
        || this.filtroLingua || this.filtroSegmento || this.filtroNivel
        || this.filtroColecao || this.filtroMarca);
    },
    tabelaPrecoId() {
      return this.$store.getters['InfoPedido/GET_FORM_INFO_PEDIDO']?.tabelaPreco?.tabelapreco_id || '';
    },
    opcoesMarcas() {
      return [{ value: null, text: 'Todas' }, ...this.filtrosOptions.marcas.map((marca) => ({
        value: marca.cod_marca,
        text: marca.des_marca,
      }))];
    },
    totalPages() {
      return this.totalProducts ? Math.ceil(this.totalProducts / this.pageSize) : 1;
    },
    // Assinatura do cabeçalho do pedido que afeta o filtro do catálogo.
    // Muda quando cliente/representante/tabela/condição/tipo de pedido mudam.
    pedidoFiltroKey() {
      const form = this.$store.getters['InfoPedido/GET_FORM_INFO_PEDIDO'] || {};
      const completo = this.$store.getters['InfoPedido/GET_VALIDATION_FORM_INFO_PEDIDO'];
      const tipo = form.tipoPedidoCliente?.tipopedidoid || '';
      // clienteid entra na chave: trocar de cliente re-filtra o catálogo (produtos
      // customizados são exclusivos por cliente) — sem isso, itens de outro cliente
      // ficariam visíveis até um novo fetch.
      const cliente = form.cliente?.clienteid || '';
      // Assinatura da lista de tipos permitidos: quando ela termina de carregar
      // (perfil Cliente busca 'WS,WC,...'), o watcher refaz o fetch com o filtro
      // correto — sem isso o cliente poderia ficar com um catálogo sem filtro.
      const tipos = this.tiposDisponiveis.map((t) => t.value).join(',');
      return `${completo ? 1 : 0}|${tipo}|${cliente}|${this.tabelaPrecoId}|${tipos}`;
    },
  },
  methods: {
    async carregarPrecosCatalogo() {
      const epoch = ++this.precoEpoch;
      this.precosCatalogo = {};
      this.precosCarregando = false;
      if (!this.contextoPrecoPronto) return;
      const form = JSON.parse(JSON.stringify(this.contextoPreco));
      const context = JSON.stringify([this.$route.params.tenantSlug,
        localStorage.getItem('perfilLogado'), localStorage.getItem('userData'), form]);
      this.precosCarregando = true;
      await Promise.all(this.itensPaginaPreco.map(async item => {
        const id = item.item_id || item.itemId || item.CodItem;
        let value = null;
        try {
          value = await cotarProdutoCatalogo(context, form, id, () => epoch === this.precoEpoch && !this._isDestroyed);
        } catch (_) { /* Hide unpriced products; offer explicit retry above. */ }
        if (epoch === this.precoEpoch && !this._isDestroyed) this.$set(this.precosCatalogo, id, value);
      }));
      if (epoch === this.precoEpoch) this.precosCarregando = false;
    },
    opcoesFiltro(campo, todos) {
      return [{ value: null, text: todos }, ...(this.filtrosOptions[campo] || [])];
    },
    async carregarFiltrosItens() {
      // Mesmo contrato da Operação. Sem tabela não há elegibilidade de preço
      // determinada, portanto não exibimos opções possivelmente incorretas.
      if (!this.tabelaPrecoId) {
        this.filtrosOptions = { lingua: [], segmento: [], niveleducacional: [], colecao: [], marcas: [] };
        return;
      }
      const precoId = this.tabelaPrecoId;
      try {
        const response = await new SLSPedidosVendasService().listarFiltrosItens(precoId);
        // A troca de tabela/tipo pode ocorrer enquanto a consulta anterior está
        // em trânsito; não deixe opções da tabela anterior sobrescreverem as novas.
        if (precoId !== this.tabelaPrecoId) return;
        const data = response?.data || {};
        this.filtrosOptions = {
          lingua: data.lingua || [],
          segmento: data.segmento || [],
          niveleducacional: data.niveleducacional || [],
          colecao: data.colecao || [],
          marcas: data.marcas || [],
        };
      } catch (error) {
        console.warn('[Home] filtros de catálogo indisponíveis:', error?.message);
      }
    },
    async fetchPage(page) {
      this.currentPage = page;

      // O catálogo NÃO exige mais o Info Pedido preenchido — os produtos são
      // exibidos direto. Regra por perfil:
      // - Cliente: vê TODOS os tipos permitidos juntos (lista 'WS,WC,...'); a
      //   distinção de categoria só acontece ao adicionar ao carrinho.
      // - Demais perfis: catálogo filtrado pelo tipo selecionado (default WS ou
      //   o único tipo do perfil, ex.: WD p/ representante/supervisor).
      const tipoPedidoId = this.tipoSelecionado;
      let tipoParam = tipoPedidoId;
      if (this.isPerfilCliente) {
        const permitidos = this.tiposDisponiveis.map((t) => t.value).filter(Boolean);
        if (!permitidos.length && !tipoPedidoId) {
          // Lista de tipos permitidos ainda carregando — NUNCA busca sem filtro
          // (mostraria o catálogo inteiro). O watcher refaz quando ela chegar.
          this.showLoading = false;
          return;
        }
        tipoParam = permitidos.length ? permitidos.join(',') : tipoPedidoId;
      } else if (!tipoPedidoId) {
        // Tipos ainda carregando (garantirTipoPedidoDefault) — o watcher de
        // pedidoFiltroKey dispara o fetch assim que o default for definido.
        this.showLoading = false;
        return;
      }

      this.showLoading = true;
      try {
        const imageParams = {
          limit: this.pageSize,
          offset: (this.currentPage - 1) * this.pageSize,
          images_limit: 5,
          // codMarca identifica a marca usada nos metadados legados, não o
          // escopo do catálogo. O select vazio significa todas as marcas.
          todas_marcas: true,
        };
        if (tipoParam) imageParams.tipopedidoid = tipoParam;

        if (this.currentItemNome.trim()) {
          imageParams.item_nome = this.currentItemNome.trim();
        }

        if (this.currentItemCodigo.trim()) {
          imageParams.item_codigo = this.currentItemCodigo.trim();
        }
        if (this.filtroLingua) imageParams.lingua = this.filtroLingua;
        if (this.filtroSegmento) imageParams.segmento = this.filtroSegmento;
        if (this.filtroNivel) imageParams.niveleducacional = this.filtroNivel;
        if (this.filtroColecao) imageParams.colecao = this.filtroColecao;
        if (this.filtroMarca) imageParams.filtro_cod_marca = this.filtroMarca;
        if (this.tabelaPrecoId) imageParams.precoId = this.tabelaPrecoId;

        // Exclusividade: quando há cliente selecionado no cabeçalho do pedido,
        // o backend só devolve produtos customizados vinculados a esse cliente.
        // Sem cliente selecionado, os customizados aparecem para todos.
        const clienteSelecionadoId = this.$store.getters['InfoPedido/GET_FORM_INFO_PEDIDO']
          ?.cliente?.clienteid;
        if (clienteSelecionadoId) {
          imageParams.clienteid = clienteSelecionadoId;
        }

        const result = await this.slsMarcaService.listarImagensPorMarca(this.codMarca, imageParams);
        const items = Array.isArray(result) ? result : result?.data || [];
        this.lastFetchCount = items.length;
        this.totalProducts = Array.isArray(result) ? items.length : (result?.total || items.length);

        // Busca por código sem resultado, mas o item EXISTE em outra categoria
        // (ex.: pedido WS e o usuário buscou um ISBN só de certificação/WC) →
        // informa a categoria do produto e orienta a fazer um novo pedido.
        // Vale para todos os perfis (não só o perfil Cliente).
        if (!items.length && result?.itemOutraCategoria) {
          let textoCategoria = 'Este produto pertence a outra categoria';
          try {
            const codigo = this.currentItemCodigo.trim();
            const { data: catResp } = await axios.post('items/categorias', {
              itemIds: [codigo],
              clienteid: this.$store.getters['InfoPedido/GET_FORM_INFO_PEDIDO']?.cliente?.clienteid,
            });
            const catItem = catResp?.data?.[codigo];
            if (catItem) {
              textoCategoria = `Este produto é da categoria ${nomeCategoria(catItem)}`;
            }
          } catch (e) {
            console.warn('[Home] categoria do item fora do filtro indisponível:', e?.message);
          }
          const categoriaAtual = this.isPerfilCliente
            ? 'diferente das categorias disponíveis para o seu perfil'
            : `diferente da categoria deste pedido (${nomeCategoria(tipoPedidoId)})`;
          Swal.fire({
            icon: 'info',
            title: 'Categoria diferente',
            text: `${textoCategoria}, ${categoriaAtual}. `
              + 'Para incluí-lo, finalize este pedido e faça um novo pedido da categoria dele.',
          });
        }

        const itemIds = items.map(i => i.item_id || i.CodItem).filter(Boolean);
        if (itemIds.length) {
          // Guarda a assinatura do pedido no momento da consulta — se o tipo de
          // pedido mudar antes da resposta chegar, descarta (estoque de outro depósito).
          const filtroKeyNoRequest = this.pedidoFiltroKey;
          estoqueService.consultarEstoque(itemIds).then(map => {
            if (this.pedidoFiltroKey !== filtroKeyNoRequest) return;
            this.estoqueMap = { ...this.estoqueMap, ...map };
          });
        }
      } catch (error) {
        console.error('Erro ao carregar página:', error);
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro',
            text: 'Erro ao carregar produtos',
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        });
      } finally {
        this.showLoading = false;
      }
    },
    async onPesquisar() {
      await this.fetchPage(1);
    },
    // Nome amigável do checkbox: 'WS - Venda', 'WD - Doação'...
    nomeTipo(t) {
      const cod = String(t.value || '').toUpperCase();
      return `${cod} - ${nomeCategoria(cod)}`;
    },
    // Checkbox exclusivo (funciona como radio): seleciona o tipo e o watcher
    // de pedidoFiltroKey refaz a busca. Clicar no já selecionado não desmarca.
    selecionarTipo(t) {
      if (this.tipoSelecionado === t.value) return;
      this.$store.commit('InfoPedido/SET_TIPO_PEDIDO_CLIENTE', {
        tipopedidoid: t.value,
        tipopedidodescricao: t.text || this.nomeTipo(t),
      });
      // Sincroniza o cache do Info Pedido no localStorage: ao abrir o modal
      // ele restaura o formulário salvo, e sem isso o tipo antigo (ex.: WS)
      // sobrescreveria a escolha feita aqui no checkbox.
      try {
        const userData = JSON.parse(localStorage.getItem('userData'));
        const userId = userData?.id || userData?.userData?.id || 'default';
        const chave = `infoPedidoForm_${userId}`;
        const salvo = localStorage.getItem(chave);
        if (salvo) {
          const dados = JSON.parse(salvo);
          dados.tipoPedidoCliente = {
            tipopedidoid: t.value,
            tipopedidodescricao: t.text || this.nomeTipo(t),
          };
          localStorage.setItem(chave, JSON.stringify(dados));
        }
      } catch (e) {
        console.error('Erro ao sincronizar tipo de pedido no cache:', e);
      }
    },
    abrirImportarCsv() {
      const contextoValido = this.$store.getters['InfoPedido/GET_VALIDATION_FORM_INFO_PEDIDO'];
      if (!contextoValido) {
        this.$bvModal.show('modal-center-info-pedido');
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Selecione o cliente',
            text: 'Escolha o cliente e a tabela de preço antes de importar o CSV.',
            icon: 'AlertCircleIcon',
            variant: 'warning',
          },
        });
        return;
      }
      this.showImportarCsv = true;
    },
    abrirExportarCatalogo() {
      this.showExportarCatalogo = true;
    },
    async onLimparFiltros() {
      this.currentItemNome = '';
      this.currentItemCodigo = '';
      this.filtroLingua = null;
      this.filtroSegmento = null;
      this.filtroNivel = null;
      this.filtroColecao = null;
      this.filtroMarca = null;
      this.currentPage = 1;
      this.$store.commit('slsMarca/setImagensPorMarca', []);
      await this.fetchPage(1);
    },
    downloadCatalogoPDF() {
      if (!this.catalogoPdfCaminho) return;
      const link = document.createElement('a');
      link.href = this.catalogoPdfCaminho;
      link.download = 'catalogo.pdf';
      link.target = '_blank';
      link.click();
    },
    async carregarCatalogoPdf() {
      try {
        const [ativo, caminho] = await Promise.all([
          slsParametrosService.findByDescricao('CATALOGO_PDF_ATIVO').catch(() => null),
          slsParametrosService.findByDescricao('CATALOGO_PDF_CAMINHO').catch(() => null),
        ]);
        const rowAtivo = ativo?.data || ativo;
        const rowCaminho = caminho?.data || caminho;
        const val = String(rowAtivo?.parametro_valor ?? '').trim().toLowerCase();
        this.catalogoPdfAtivo = ['s', 'sim', 'true', '1', 'y', 'yes'].includes(val);
        this.catalogoPdfCaminho = rowCaminho?.parametro_valor || '';
      } catch (error) {
        this.catalogoPdfAtivo = false;
        this.catalogoPdfCaminho = '';
      }
    },
    onChangePage(page) {
      // b-pagination já atualizou currentPage via v-model antes de emitir @change;
      // por isso não comparamos page === currentPage (sempre teria suprimido a busca).
      this.fetchPage(page);
    },
    async getPrioridadePadraoPedido() {
      try {
        await slsEsPrioriService.selectPrioridadePedido();
        const prioridadePadrao = await slsParametrosService.findByDescricao('PARAM_PRIORIDADE_PADRAO');
        if (prioridadePadrao) {
          this.$store.commit('InfoPedido/SET_PRIORIDADE_PEDIDO_FORM', prioridadePadrao.parametro_valor);
        }
      } catch (error) {
        console.error('Erro ao obter prioridade padrão do pedido:', error);
      }
    },
  },
  async mounted() {
    this.showLoading = true;
    try {
      homeService.loadingDadosHome();
      this.getPrioridadePadraoPedido();
      this.carregarCatalogoPdf();

      // Perfil Cliente: repara dados incompletos (condição pgto/tabela/estabelecimento/
      // imposto) sem exigir a abertura do modal Info Pedido, e garante o tipo de
      // pedido default (WS) — sem tipo selecionado o catálogo fica bloqueado.
      await InitialDataService.ensureClienteFinalDataLoaded();
      await this.$store.dispatch('InfoPedido/garantirTipoPedidoDefault').catch(() => {});

      await this.slsMarcaService.findAll();
      const marcas = this.$store.getters['slsMarca/getEsMarcas'];
      if (marcas && marcas.length > 0) {
        this.codMarca = marcas[0].cod_marca;
      }

      const q = this.$route && this.$route.query ? this.$route.query : {};
      const nomeQuery = q.nome || q.item_nome || null;
      const codigoQuery = q.codigo || q.item_codigo || null;
      if (nomeQuery) {
        this.currentItemNome = Array.isArray(nomeQuery) ? String(nomeQuery[0]) : String(nomeQuery);
      }
      if (codigoQuery) {
        this.currentItemCodigo = Array.isArray(codigoQuery) ? String(codigoQuery[0]) : String(codigoQuery);
      }

      if (this.codMarca) {
        await this.carregarFiltrosItens();
        await Promise.all([
          this.slsMarcaService.obterMarcarPorCodMarca(this.codMarca),
          this.slsMarcaService.listarCategoriasPorMarca(this.codMarca),
        ]);
        await this.fetchPage(1);
      }
    } catch (error) {
      console.error('Erro ao carregar catálogo na home:', error);
    } finally {
      this.showLoading = false;
    }
  },
  watch: {
    chavePrecosCatalogo: {
      immediate: true,
      handler() { this.carregarPrecosCatalogo(); },
    },
    pedidoFiltroKey() {
      // Tipo de pedido mudou (venda ↔ doação) → estoque exibido muda de depósito;
      // limpa o mapa pra não misturar quantidades do tipo anterior.
      this.estoqueMap = {};
      this.carregarFiltrosItens();
      if (this.codMarca) this.fetchPage(1);
    },
    tabelaPrecoId() {
      this.filtroLingua = null;
      this.filtroSegmento = null;
      this.filtroNivel = null;
      this.filtroColecao = null;
      this.filtroMarca = null;
      this.carregarFiltrosItens();
    },
    '$route.query.nome': function (val) {
      const novo = Array.isArray(val) ? (val[0] || '') : (val || '');
      if (novo !== this.currentItemNome) {
        this.currentItemNome = String(novo);
        if (this.codMarca) this.fetchPage(1);
      }
    },
    '$route.query.codigo': function (val) {
      const novo = Array.isArray(val) ? (val[0] || '') : (val || '');
      if (novo !== this.currentItemCodigo) {
        this.currentItemCodigo = String(novo);
        if (this.codMarca) this.fetchPage(1);
      }
    },
    '$route.query.item_nome': function (val) {
      const novo = Array.isArray(val) ? (val[0] || '') : (val || '');
      if (novo !== this.currentItemNome) {
        this.currentItemNome = String(novo);
        if (this.codMarca) this.fetchPage(1);
      }
    },
    '$route.query.item_codigo': function (val) {
      const novo = Array.isArray(val) ? (val[0] || '') : (val || '');
      if (novo !== this.currentItemCodigo) {
        this.currentItemCodigo = String(novo);
        if (this.codMarca) this.fetchPage(1);
      }
    },
  },
  destroyed() {
    this.$store.commit('slsMarca/setImagensPorMarca', []);
  },
};
</script>

<style scoped lang="scss">
// ============================================
// HOME PAGE — Inline Product Catalog
// All colors via CSS custom properties
// ============================================

.home-page {
  background: var(--sf-body-bg, #f5f7fa);
  min-height: 100vh;
}

// ============================================
// HERO
// ============================================
.hero-section {
  background: linear-gradient(135deg, #1a1b20 0%, #0f1013 100%);
  padding: 2.75rem 2rem;
  margin: -1.5rem -1.5rem 0 -1.5rem;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, rgba(0,0,0,.45) 0%, rgba(0,0,0,.25) 100%);
    z-index: 0;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    opacity: .5;
    z-index: 0;
  }
}

.hero-content {
  position: relative;
  z-index: 2;
}

.hero-greeting {
  font-size: 1.1rem;
  font-weight: 600;
  color: rgba(255,255,255,.9);
  margin-bottom: .25rem;
}

.hero-title {
  font-size: 2.5rem;
  font-weight: 800 !important;
  color: rgba(255,255,255,.98) !important;
  margin-bottom: .5rem;
  letter-spacing: -.5px;
  text-shadow: 0 2px 8px rgba(0,0,0,.5), 0 4px 16px rgba(0,0,0,.3) !important;
}

.hero-subtitle {
  font-size: 1.05rem;
  color: rgba(255,255,255,.8) !important;
  margin: 0;
  font-weight: 500 !important;
  text-shadow: 0 1px 6px rgba(0,0,0,.4) !important;
}

// ============================================
// CATALOG SECTION — white panel rising from hero
// ============================================
.catalog-section {
  max-width: 1400px;
  margin: -1.5rem auto 0;
  background: var(--sf-surface-bg, #ffffff);
  border-radius: 20px 20px 16px 16px;
  border: 1px solid var(--sf-border-light, #e5e8f0);
  position: relative;
  z-index: 2;
  box-shadow: 0 -4px 30px rgba(0,0,0,.07), 0 8px 32px rgba(0,0,0,.04);
  padding-bottom: 2rem;
}

// ============================================
// ACTION BAR — filters row
// ============================================
.catalog-action-bar {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.5rem 1.75rem 1.25rem;
  border-bottom: 1px solid var(--sf-border-light, #e5e8f0);
  flex-wrap: wrap;
}

.action-bar-filters {
  display: flex;
  align-items: flex-end;
  gap: 1rem;
  flex-wrap: wrap;
  flex: 1;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: .45rem;
  min-width: 190px;
  flex: 1;
  max-width: 260px;
}

.filter-label {
  font-size: .75rem;
  font-weight: 700;
  color: var(--primary, #2563eb);
  text-transform: uppercase;
  letter-spacing: .06em;
  margin: 0;
}

.filter-select {
  ::v-deep .vs__dropdown-toggle {
    background: var(--sf-form-bg, #ffffff);
    border: 1px solid var(--sf-form-border, #e0e4f0);
    border-radius: 8px;
    padding: .375rem .5rem;
    min-height: 42px;
    transition: border-color .2s, box-shadow .2s;
  }

  ::v-deep .vs__dropdown-toggle:focus-within {
    border-color: var(--sf-form-focus-border, var(--primary, #2563eb));
    box-shadow: var(--sf-form-focus-shadow, 0 0 0 3px rgba(37,99,235,.15));
  }

  ::v-deep .vs__search,
  ::v-deep .vs__search::placeholder {
    color: var(--sf-form-placeholder, #94a3b8);
  }

  ::v-deep .vs__selected {
    color: var(--sf-form-text, #0f172a);
    font-weight: 500;
  }

  ::v-deep .vs__open-indicator,
  ::v-deep .vs__clear {
    fill: var(--sf-form-text, #0f172a);
  }

  ::v-deep .vs__dropdown-menu {
    background: var(--sf-form-bg, #ffffff);
    border: 1px solid var(--sf-form-border, #e0e4f0);
    box-shadow: 0 8px 24px rgba(0,0,0,.15);
    border-radius: 8px;
    margin-top: 4px;
  }

  ::v-deep .vs__dropdown-option {
    color: var(--sf-form-text, #0f172a);
  }

  ::v-deep .vs__dropdown-option--highlight {
    background: var(--sf-accent-bg, rgba(37,99,235,.1));
    color: var(--primary, #2563eb);
    font-weight: 600;
  }
}

.filter-input {
  background: var(--sf-form-bg, #ffffff) !important;
  border: 1px solid var(--sf-form-border, #e0e4f0) !important;
  border-radius: 8px !important;
  padding: .5rem .75rem !important;
  font-size: .9rem;
  height: 42px;
  color: var(--sf-form-text, #0f172a) !important;
  font-weight: 500;
  transition: border-color .2s, box-shadow .2s;

  &::placeholder {
    color: var(--sf-form-placeholder, #94a3b8) !important;
    font-weight: 400;
    opacity: 1;
  }

  &:focus {
    border-color: var(--sf-form-focus-border, var(--primary, #2563eb)) !important;
    box-shadow: var(--sf-form-focus-shadow, 0 0 0 3px rgba(37,99,235,.15)) !important;
  }
}

.filter-buttons {
  display: flex;
  gap: .75rem;
  align-items: center;
}

.btn-filter-action {
  display: inline-flex;
  align-items: center;
  gap: .45rem;
  padding: .5rem 1.2rem;
  font-size: .88rem;
  font-weight: 600;
  border-radius: 8px;
  height: 42px;
  transition: all .2s ease;
  white-space: nowrap;
}

.btn-primary-action {
  background: var(--primary, #2563eb) !important;
  border-color: var(--primary, #2563eb) !important;
  color: #fff !important;
  box-shadow: 0 4px 14px rgba(var(--primary-rgb, 37,99,235), .3);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(var(--primary-rgb, 37,99,235), .4);
    filter: brightness(1.07);
  }

  &:disabled {
    opacity: .5;
    cursor: not-allowed;
    box-shadow: none;
  }
}

.btn-secondary-action {
  background: var(--sf-surface-bg, #ffffff) !important;
  border: 1px solid var(--sf-border-light, #e0e4f0) !important;
  color: var(--primary, #2563eb) !important;

  &:hover:not(:disabled) {
    background: var(--sf-accent-bg, rgba(37,99,235,.07)) !important;
    border-color: var(--primary, #2563eb) !important;
  }

  &:disabled {
    opacity: .45;
    cursor: not-allowed;
  }
}

.action-bar-right {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-shrink: 0;
}

// Filtro por tipo de pedido (checkbox exclusivo) na barra de ações
.tipo-filter {
  display: flex;
  align-items: center;
  gap: .85rem;
  padding: .35rem .75rem;
  border: 1px solid var(--sf-border-light, #e0e4f0);
  border-radius: 8px;
  background: var(--sf-surface-bg, #fff);
}

.tipo-filter-item {
  display: inline-flex;
  align-items: center;
  gap: .35rem;
  margin: 0;
  font-size: .85rem;
  font-weight: 600;
  color: var(--sf-text-primary, #2f2b3d);
  cursor: pointer;
  white-space: nowrap;

  input {
    cursor: pointer;
    accent-color: var(--primary, #2563eb);
  }
}

.products-count {
  font-size: .88rem;
  color: var(--sf-text-muted, #5f6472);
  font-weight: 500;
  white-space: nowrap;

  strong {
    font-weight: 800;
    color: var(--primary, #2563eb);
  }
}

.btn-download {
  display: inline-flex;
  align-items: center;
  gap: .45rem;
  padding: .5rem 1.1rem;
  font-size: .88rem;
  font-weight: 600;
  border-radius: 8px;
  height: 40px;
  background: var(--sf-surface-bg, #fff) !important;
  border: 1px solid var(--sf-border-light, #e0e4f0) !important;
  color: var(--primary, #2563eb) !important;
  transition: all .2s ease;

  &:hover {
    background: var(--sf-accent-bg, rgba(37,99,235,.07)) !important;
    border-color: var(--primary, #2563eb) !important;
    box-shadow: 0 4px 12px rgba(var(--primary-rgb, 37,99,235), .12);
  }
}

// ============================================
// PRODUCT GRID
// ============================================
.products-section {
  padding: 1.5rem 1.75rem 0;
}

.products-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 1.5rem;
}

.product-grid-item {
  min-width: 0;
  animation: fadeInUp .35s ease-out both;

  @for $i from 1 through 8 {
    &:nth-child(#{$i}) {
      animation-delay: #{($i - 1) * 0.04}s;
    }
  }
}

@keyframes fadeInUp {
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
}

// ============================================
// EMPTY STATE
// ============================================
.empty-state {
  text-align: center;
  padding: 4rem 2rem;
  background: var(--sf-surface-secondary, #f5f7fa);
  border-radius: 12px;
  border: 1px solid var(--sf-border-light, #e5e8f0);
}

.empty-icon {
  color: var(--sf-border-light, #c8cfdb);
  margin-bottom: 1.25rem;
}

.empty-title {
  font-size: 1.2rem;
  font-weight: 700;
  color: var(--sf-text-primary, #2f2b3d);
  margin-bottom: .5rem;
}

.empty-text {
  font-size: .92rem;
  color: var(--sf-text-muted, #5f6472);
  margin-bottom: 1.5rem;
}

.btn-reset,
.btn-abrir-info-pedido {
  display: inline-flex;
  align-items: center;
  gap: .45rem;
  padding: .55rem 1.25rem;
  font-size: .9rem;
  font-weight: 600;
  border-radius: 8px;
  background: var(--primary, #2563eb) !important;
  border-color: var(--primary, #2563eb) !important;
  color: #fff !important;
  box-shadow: 0 4px 14px rgba(var(--primary-rgb, 37,99,235), .28);
  transition: transform .2s;

  &:hover {
    transform: translateY(-2px);
    filter: brightness(1.07);
  }
}

// ============================================
// PAGINATION
// ============================================
.catalog-pagination {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.5rem 1.75rem 0;
  flex-wrap: wrap;
}

.pagination-info {
  font-size: .88rem;
  color: var(--sf-text-muted, #5f6472);
  font-weight: 600;
}

.pagination-actions {
  display: flex;
  align-items: center;
  gap: .75rem;
}

.btn-pagination {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: .45rem;
  padding: .5rem 1.2rem;
  font-size: .88rem;
  font-weight: 600;
  border-radius: 8px;
  background: var(--sf-surface-bg, #fff) !important;
  border: 1px solid var(--sf-border-light, #e0e4f0) !important;
  color: var(--primary, #2563eb) !important;
  transition: all .2s ease;

  &:hover:not(:disabled) {
    background: var(--sf-accent-bg, rgba(37,99,235,.07)) !important;
    border-color: var(--primary, #2563eb) !important;
    box-shadow: 0 4px 10px rgba(var(--primary-rgb, 37,99,235), .1);
  }

  &:disabled {
    opacity: .45;
    cursor: not-allowed;
  }
}

// ============================================
// RESPONSIVE
// ============================================
@media (max-width: 1200px) {
  .catalog-section {
    margin-left: 1rem;
    margin-right: 1rem;
  }
}

@media (max-width: 992px) {
  .hero-title { font-size: 2rem; }

  .catalog-action-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .action-bar-filters {
    width: 100%;
  }

  .action-bar-right {
    justify-content: space-between;
    width: 100%;
  }

  .filter-field {
    max-width: 100%;
  }
}

@media (max-width: 768px) {
  .hero-section {
    padding: 2.25rem 1.5rem;
    margin: -1rem -1rem 0 -1rem;
  }

  .hero-title { font-size: 1.75rem; }

  .catalog-section {
    margin-left: .5rem;
    margin-right: .5rem;
    border-radius: 16px 16px 12px 12px;
  }

  .products-section,
  .catalog-action-bar,
  .catalog-pagination {
    padding-left: 1.25rem;
    padding-right: 1.25rem;
  }

  .products-grid {
    grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
    gap: 1rem;
  }

  .filter-field {
    min-width: 100%;
  }
}

@media (max-width: 576px) {
  .hero-title  { font-size: 1.5rem; }
  .hero-subtitle { font-size: .9rem; }

  .products-grid {
    grid-template-columns: 1fr 1fr;
    gap: .75rem;
  }

  .catalog-action-bar,
  .products-section,
  .catalog-pagination {
    padding-left: 1rem;
    padding-right: 1rem;
  }
}
</style>
